import React from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import startOfMonth from "date-fns/startOfMonth";
import formatDate from "date-fns/format";
import { formatCurrency } from "common/format";
import {
  GET_PAYER_MONTHLY_CHARGES,
  GetPayerMonthlyChargesData,
  GetPayerMonthlyChargesVars,
} from "common/graphql/queries/GetPayerMonthlyCharges";
import { buildPaymentTransactionsWhere } from "common/graphql/queries/GetPaymentTransactions";
import { parseFilterModelFromSearchParams } from "common/graphql/queryString";
import LoadingSkeleton from "@app.automotus.io/components/LoadingSkeleton";
import { useTheme } from "@mui/material/styles";

export interface PayerOverviewProps {
  /** Current wallet balance snapshot in cents. When undefined the wallet card is hidden (e.g. session-preference users). */
  walletBalance?: number;
}

interface ScorecardProps {
  title: React.ReactNode;
  subtitle?: string;
  description: string;
  value: string;
  loading?: boolean;
  accentColor: string;
  amountColor?: string;
  iconBgColor: string;
  icon: React.ReactNode;
}

const Scorecard: React.FC<ScorecardProps> = ({
  title,
  subtitle,
  description,
  value,
  loading,
  accentColor,
  amountColor,
  iconBgColor,
  icon,
}) => (
  <Paper
    elevation={1}
    sx={{
      flex: 1,
      borderRadius: 2,
      borderTop: `4px solid ${accentColor}`,
      p: { xs: 2, md: 3 },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
      <Box
        sx={{
          bgcolor: iconBgColor,
          borderRadius: 1.5,
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="overline"
          sx={{ fontWeight: 600, lineHeight: 1.2, display: "block", color: "text.secondary" }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <LoadingSkeleton loading={Boolean(loading)}>
      <Typography
        variant="h3"
        sx={{
          color: amountColor || "text.primary",
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: "2rem", md: "2.5rem" },
        }}
      >
        ${value}
      </Typography>
    </LoadingSkeleton>
    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
      {description}
    </Typography>
  </Paper>
);

export const PayerOverview: React.FC<PayerOverviewProps> = ({ walletBalance }) => {
  const theme = useTheme();
  const [now] = React.useState(new Date());
  const [searchParams] = useSearchParams();
  const dateStr = searchParams.get("date") || startOfMonth(now).toISOString();
  const selectedDate = React.useMemo(() => new Date(dateStr), [dateStr]);
  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const chargesWhere = React.useMemo(
    () => buildPaymentTransactionsWhere(selectedDate, appliedFilters),
    [selectedDate, appliedFilters],
  );

  const { data, loading } = useQuery<GetPayerMonthlyChargesData, GetPayerMonthlyChargesVars>(
    GET_PAYER_MONTHLY_CHARGES,
    { variables: { where: chargesWhere }, fetchPolicy: "network-only" },
  );

  // Park prices are stored as negatives; gateway charges (session_payment, permit) are positive.
  // Sum each group separately and add absolute values to avoid sign cancellation.
  const parkCharges = Math.abs(data?.parkChargesAggregate.aggregate.sum.price ?? 0);
  const gatewayCharges = data?.gatewayChargesAggregate.aggregate.sum.price ?? 0;
  const charges = parkCharges + gatewayCharges;
  const monthLabel = formatDate(selectedDate, "MMMM yyyy");

  return (
    <Box sx={{ bgcolor: "background.paper", py: { xs: 3, lg: 3.75 } }}>
      <Container maxWidth="xl" sx={{ display: "flex", gap: 2 }}>
        {walletBalance !== undefined && (
          <Scorecard
            title={
              <>
                Wallet
                <br />
                Balance
              </>
            }
            description="Current available balance"
            value={formatCurrency(walletBalance)}
            accentColor={theme.palette.primary.main}
            amountColor={theme.palette.primary.main}
            iconBgColor={`${theme.palette.primary.main}1A`}
            icon={<AccountBalanceWalletOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />}
          />
        )}
        <Scorecard
          title={
            <>
              Monthly
              <br />
              Charges
            </>
          }
          description={`Charged ${monthLabel}`}
          value={formatCurrency(charges)}
          loading={loading}
          accentColor={theme.palette.grey[400]}
          iconBgColor={theme.palette.grey[100]}
          icon={<ReceiptLongOutlinedIcon sx={{ color: theme.palette.text.secondary, fontSize: 24 }} />}
        />
      </Container>
    </Box>
  );
};

export default PayerOverview;
